import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useDragScroll from '../../../hooks/useDragScroll';

// `#`: names that don't start with a letter (emoji, symbol, digit) or no name.
export const INITIALS = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''), '#'];

/**
 * A–Z (+ #) card that filters a list by the first letter of the name; the active
 * letter is marked in `secondary`, and clicking it again clears it
 * (`onChange('')`). Sits as its own card under the filters card.
 *
 * The letters are centered when they fit; when they don't, the row scrolls
 * sideways — touch scrolls natively, the mouse drags (useDragScroll), with no
 * scrollbar, like the mobile list chips.
 */
export default function InitialFilterBar({ value, onChange, label }) {
  const dragScroll = useDragScroll();
  const row = useRef(null);

  // Keeps the active letter in view when the row scrolls (e.g. on a phone).
  useEffect(() => {
    const container = row.current;
    const button = value && container?.querySelector(`[value="${value}"]`);
    if (!button) return;
    const box = container.getBoundingClientRect();
    const target = button.getBoundingClientRect();
    container.scrollLeft += target.left + target.width / 2 - (box.left + box.width / 2);
  }, [value]);

  return (
    <Card sx={{ flexShrink: 0 }}>
      <Box
        ref={row}
        {...dragScroll}
        sx={{
          overflowX: 'auto',
          userSelect: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          p: 1,
        }}
      >
        {/* max-content + auto margins: centered when it fits, scrollable from the left when it doesn't. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          color="secondary"
          value={value || null}
          onChange={(event, next) => onChange(next ?? '')}
          aria-label={label}
          sx={{ display: 'flex', width: 'max-content', mx: 'auto' }}
        >
          {INITIALS.map((letter) => (
            <ToggleButton key={letter} value={letter} sx={{ minWidth: 36 }}>
              {letter}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Card>
  );
}
